import React from 'react'

import { ApolloProvider } from '@apollo/client'
import { useEditor } from '@toasttab/sites-components'

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { PopoverContextProvider } from 'shared/components/common/popover/PopoverContext'

import { DebugPanel } from 'src/public/components/default_template/debug/DebugPanel'
import { CART_POPOVER_CONTEXT_KEY } from 'src/public/components/default_template/online_ordering/cart/CartModal'
import { useSpiSdk } from 'src/public/components/default_template/online_ordering/checkout/payment/useSpi'
import Popups from 'src/public/components/default_template/popups/Popups'
import { MenuSearchContextProvider } from 'src/public/components/default_template/search'
import { CartContextProvider } from 'src/public/components/online_ordering/CartContext'
import { DeliveryContextProvider } from 'src/public/components/online_ordering/DeliveryContext'
import {
  EditorFulfillmentContextProvider,
  FulfillmentContextProvider
} from 'src/public/components/online_ordering/FulfillmentContext'
import { LoyaltyContextProvider } from 'src/public/components/online_ordering/LoyaltyContext'
import OffersContextProvider from 'src/public/components/online_ordering/OffersContext'
import { TimeBasedRuleContextProvider } from 'src/public/components/online_ordering/TimeBasedRuleContext'
import { UpsellContextProvider } from 'src/public/components/online_ordering/UpsellContext'
import { Channel } from 'src/public/js/siteUtilities'
import { A11yProvider } from './useA11y'

type Props = {
  channel: Channel
  isOrderPage?: boolean
}

/**
 * Shared component that provides all OO-specific contexts.
 * Used by both OrderPageContexts and EcommerceProvidersWrapper to avoid duplication.
 *
 * Provides:
 * - ApolloProvider (OO client)
 * - CartContext, DeliveryContext, MenuSearchContext
 * - OffersContext, LoyaltyContext
 * - FulfillmentContext, TimeBasedRuleContext, UpsellContext
 * - PopoverContext, Popups, DebugPanel
 */
export const OnlineOrderingProviders = ({
  channel,
  isOrderPage = false,
  children
}: React.PropsWithChildren<Props>) => {
  const ooClient = useOOClient()
  const { isEditor } = useEditor()

  // Pre-load SPI SDK for checkout
  useSpiSdk(isEditor)

  return (
    <ApolloProvider client={ooClient}>
      <A11yProvider>
        <CartContextProvider channel={channel}>
          <DeliveryContextProvider>
            <MenuSearchContextProvider>
              <OffersContextProvider>
                <LoyaltyContextProvider>
                  {isEditor ? (
                    <EditorFulfillmentContextProvider>
                      <TimeBasedRuleContextProvider>
                        <PopoverContextProvider
                          contextKey={CART_POPOVER_CONTEXT_KEY}
                        >
                          {children}
                        </PopoverContextProvider>
                      </TimeBasedRuleContextProvider>
                    </EditorFulfillmentContextProvider>
                  ) : (
                    <FulfillmentContextProvider>
                      <TimeBasedRuleContextProvider>
                        <UpsellContextProvider>
                          <PopoverContextProvider
                            contextKey={CART_POPOVER_CONTEXT_KEY}
                          >
                            <Popups isOrderPage={isOrderPage} />
                            {children}
                            <DebugPanel />
                          </PopoverContextProvider>
                        </UpsellContextProvider>
                      </TimeBasedRuleContextProvider>
                    </FulfillmentContextProvider>
                  )}
                </LoyaltyContextProvider>
              </OffersContextProvider>
            </MenuSearchContextProvider>
          </DeliveryContextProvider>
        </CartContextProvider>
      </A11yProvider>
    </ApolloProvider>
  )
}
